import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import {
  ACCEPTED_GALLERY_MIME_TYPES,
  MAX_GALLERY_UPLOAD_BYTES,
  completeGalleryUpload,
  configureGalleryAlbum,
  createProduct,
  getAdminAlbumMedia,
  getGalleryAlbumMedia,
  initiateGalleryUpload,
  setAlbumPreviews,
  updateProduct,
  type GalleryAlbum,
  type GalleryMedia,
} from "../../../api/tournament/galleryService";
import { useUploadQueue, type CompleteResult } from "./useUploadQueue";
import { useProcessingPoller } from "./useProcessingPoller";

import "./Gallery.css";

const MAX_FREE_PREVIEWS = 10;

interface PendingFile {
  file: File;
  isFreePreview: boolean;
}

const AlbumDetail: React.FC = () => {
  const { tournamentId, seasonId, albumId } = useParams<{
    tournamentId: string;
    seasonId: string;
    albumId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [album, setAlbum] = useState<GalleryAlbum | null>(null);
  const [items, setItems] = useState<GalleryMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Access / pricing form state
  const [accessType, setAccessType] = useState<"public" | "paid">("paid");
  const [priceInr, setPriceInr] = useState("");
  const [priceUsd, setPriceUsd] = useState("");
  const [allowDownload, setAllowDownload] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  // Upload form state
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Free preview selection state
  const [selectedPreviewIds, setSelectedPreviewIds] = useState<string[]>([]);
  const [savingPreviews, setSavingPreviews] = useState(false);

  const backPath =
    (location.state as { returnPath?: string })?.returnPath ??
    `/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}`;

  // Album access/pricing fields come from the customer endpoint; the admin
  // endpoint below is only used for the upload/media grid, since it's the
  // one that surfaces non-ready and failed uploads too (guide §15).
  const fetchAlbumSettings = useCallback(async () => {
    if (!albumId) return;
    const res = await getGalleryAlbumMedia(albumId, 1, 1);
    setAlbum(res.album);
    setAccessType(res.album.accessType);
    setAllowDownload(res.album.allowDownload);
    setPriceInr(res.album.product?.pricing?.IN?.amount?.toString() ?? "");
    setPriceUsd(res.album.product?.pricing?.US?.amount?.toString() ?? "");
    setSelectedPreviewIds(res.album.freePreviewMediaIds ?? []);
  }, [albumId]);

  const fetchAdminMedia = useCallback(async () => {
    if (!albumId) return [] as GalleryMedia[];
    const res = await getAdminAlbumMedia(albumId, 1, 50);
    setItems(res.items);
    return res.items;
  }, [albumId]);

  const fetchAlbum = useCallback(async () => {
    if (!albumId) return;
    setLoading(true);
    setLoadError(null);
    try {
      await Promise.all([fetchAlbumSettings(), fetchAdminMedia()]);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load the album.");
    } finally {
      setLoading(false);
    }
  }, [albumId, fetchAlbumSettings, fetchAdminMedia]);

  useEffect(() => {
    fetchAlbum();
  }, [fetchAlbum]);

  const handleCompleteUpload = useCallback(
    async (mediaId: string): Promise<CompleteResult> => {
      const result = await completeGalleryUpload(mediaId);
      return { status: result.status };
    },
    [],
  );

  const {
    states: uploadStates,
    run: runUploadQueue,
    reset: resetUploadQueue,
    setItemStatus,
  } = useUploadQueue(handleCompleteUpload);
  const { start: startPolling } = useProcessingPoller();

  const saveAccessSettings = async () => {
    if (!album || !albumId) return;
    setSavingAccess(true);
    setAccessError(null);
    try {
      if (accessType === "public") {
        await configureGalleryAlbum(albumId, {
          accessType: "public",
          productId: null,
          allowDownload,
        });
      } else {
        const amount = Number(priceInr);
        if (!amount || amount <= 0) {
          throw new Error("Enter a valid INR price for a paid album.");
        }
        const pricing: Record<string, { amount: number; currency: string }> = {
          IN: { amount, currency: "INR" },
        };
        if (priceUsd && Number(priceUsd) > 0) {
          pricing.US = { amount: Number(priceUsd), currency: "USD" };
        }

        const productPayload = {
          name: `${album.name} Gallery`,
          type: "one_time" as const,
          status: "active" as const,
          applies_to: ["gallery_album"],
          target_resource_keys: [album.resourceKey],
          features: ["gallery_view"],
          pricing,
          visibility: {
            allowed_modules: ["gallery"],
            geo_allow_list: [],
            geo_block_list: [],
            applies_to_resource_types: ["gallery_album"],
          },
          revenue_split: { platform: 100, league: 0, academy: 0 },
        };

        const product = album.product
          ? await updateProduct(album.product.id, { pricing })
          : await createProduct(productPayload);
        const productId = product.id ?? product._id;
        if (!productId) throw new Error("The product could not be created.");

        await configureGalleryAlbum(albumId, {
          accessType: "paid",
          productId,
          allowDownload,
        });
      }
      toast.success("Album access settings saved.");
      await fetchAlbumSettings();
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : "Failed to save access settings.");
    } finally {
      setSavingAccess(false);
    }
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const valid: PendingFile[] = [];
    const rejected: string[] = [];
    files.forEach((file) => {
      if (!ACCEPTED_GALLERY_MIME_TYPES.includes(file.type)) {
        rejected.push(`${file.name} (unsupported type)`);
        return;
      }
      if (file.size > MAX_GALLERY_UPLOAD_BYTES) {
        rejected.push(`${file.name} (larger than 20 MB)`);
        return;
      }
      valid.push({ file, isFreePreview: false });
    });
    setPendingFiles((prev) => [...prev, ...valid]);
    setUploadError(rejected.length ? `Skipped: ${rejected.join(", ")}` : null);
    event.target.value = "";
  };

  const togglePendingFreePreview = (index: number) => {
    setPendingFiles((prev) =>
      prev.map((item, i) => (i === index ? { ...item, isFreePreview: !item.isFreePreview } : item)),
    );
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const startUpload = async () => {
    if (!album || !albumId || pendingFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    resetUploadQueue();
    try {
      const initiated = await Promise.all(
        pendingFiles.map(async ({ file, isFreePreview }) => {
          const init = await initiateGalleryUpload({
            fileName: file.name,
            contentType: file.type,
            category: album.category.id,
            subcategory: albumId,
            module: tournamentId ?? "",
            tournament: tournamentId,
            tournamentSeason: seasonId,
            isFreePreview,
          });
          return {
            id: init.mediaId,
            file,
            isFreePreview,
            uploadUrl: init.uploadUrl,
            requiredHeaders: init.requiredHeaders,
          };
        }),
      );
      await runUploadQueue(initiated);
      setPendingFiles([]);

      // `complete` only queues background processing (202) — refresh the
      // admin listing and poll every still-processing upload until each
      // settles to ready/failed (guide §15).
      const freshItems = await fetchAdminMedia();
      const uploadedIds = new Set(initiated.map((i) => i.id));
      const pendingIds = freshItems
        .filter(
          (item) =>
            uploadedIds.has(item.id) &&
            item.status &&
            item.status !== "ready" &&
            item.status !== "failed",
        )
        .map((item) => item.id);

      if (pendingIds.length > 0) {
        startPolling(
          pendingIds,
          async () =>
            (await fetchAdminMedia()).map((item) => ({
              id: item.id,
              status: item.status ?? "ready",
              processingError: item.processingError,
            })),
          (id, status, processingError) => {
            setItemStatus(id, status === "ready" ? "ready" : "failed", processingError);
          },
        );
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Failed to start the upload.");
    } finally {
      setUploading(false);
    }
  };

  const togglePreviewSelection = (media: GalleryMedia) => {
    // Only a fully processed image can be selected as a free preview
    // (guide §15: previews must belong to ready media).
    if (media.status && media.status !== "ready") return;
    setSelectedPreviewIds((prev) => {
      if (prev.includes(media.id)) return prev.filter((id) => id !== media.id);
      if (prev.length >= MAX_FREE_PREVIEWS) {
        toast.warn(`You can select up to ${MAX_FREE_PREVIEWS} free preview photos.`);
        return prev;
      }
      return [...prev, media.id];
    });
  };

  const savePreviews = async () => {
    if (!albumId) return;
    setSavingPreviews(true);
    try {
      await setAlbumPreviews(albumId, selectedPreviewIds);
      toast.success("Free previews updated.");
      await fetchAlbumSettings();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update previews.");
    } finally {
      setSavingPreviews(false);
    }
  };

  const readyCount = useMemo(
    () => items.filter((m) => !m.status || m.status === "ready").length,
    [items],
  );

  // Matches the card look used elsewhere on the tournament/match admin
  // pages (e.g. MatchDetailPage, MatchGallerySection) so this standalone
  // route doesn't look visually flat/unstyled next to them.
  const cardStyle = {
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  };
  const cardHeaderStyle = {
    backgroundColor: "#f8f9fc",
    borderBottom: "1px solid #e0e0e0",
  };

  if (loading) {
    return <ShimmerLoader type="table" rows={6} />;
  }

  if (loadError || !album) {
    return <Alert variant="danger">{loadError ?? "Album not found."}</Alert>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          items={[
            {
              label: "Gallery",
              onClick: () => navigate(backPath, { state: { activeTab: "gallery" } }),
            },
            { label: album.name, active: true },
          ]}
        />

        <Button variant="link" className="px-0 mb-3" onClick={() => navigate(backPath, { state: { activeTab: "gallery" } })}>
          <i className="ph-arrow-left me-1" /> Back to gallery
        </Button>

        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={cardHeaderStyle}>
            <h5 className="mb-0">Access &amp; Pricing</h5>
          </Card.Header>
          <Card.Body>
            {accessError && <Alert variant="danger">{accessError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Check
                inline
                type="radio"
                id="access-public"
                label="Public (free for everyone)"
                checked={accessType === "public"}
                onChange={() => setAccessType("public")}
              />
              <Form.Check
                inline
                type="radio"
                id="access-paid"
                label="Paid"
                checked={accessType === "paid"}
                onChange={() => setAccessType("paid")}
              />
            </Form.Group>

            {accessType === "paid" && (
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Label>Price (INR)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={priceInr}
                    onChange={(e) => setPriceInr(e.target.value)}
                    placeholder="199"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Price (USD, optional)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={priceUsd}
                    onChange={(e) => setPriceUsd(e.target.value)}
                    placeholder="3"
                  />
                </Col>
              </Row>
            )}

            <Form.Check
              type="switch"
              id="allow-download"
              className="mb-3"
              label="Allow download"
              checked={allowDownload}
              onChange={(e) => setAllowDownload(e.target.checked)}
            />

            <Button onClick={saveAccessSettings} disabled={savingAccess}>
              {savingAccess ? "Saving…" : "Save Access Settings"}
            </Button>
          </Card.Body>
        </Card>

        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={cardHeaderStyle}>
            <h5 className="mb-0">Upload Photos</h5>
          </Card.Header>
          <Card.Body>
            {uploadError && <Alert variant="warning">{uploadError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Control
                type="file"
                accept={ACCEPTED_GALLERY_MIME_TYPES.join(",")}
                multiple
                onChange={handleFilesSelected}
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                JPEG, PNG, WebP or AVIF — up to 20 MB each.
              </Form.Text>
            </Form.Group>

            {pendingFiles.map((item, index) => (
              <div key={`${item.file.name}-${index}`} className="gallery-upload-row">
                <span className="flex-grow-1">{item.file.name}</span>
                <Form.Check
                  type="checkbox"
                  label="Free preview"
                  checked={item.isFreePreview}
                  onChange={() => togglePendingFreePreview(index)}
                  disabled={uploading}
                />
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => removePendingFile(index)}
                  disabled={uploading}
                >
                  Remove
                </Button>
              </div>
            ))}

            {pendingFiles.length > 0 && (
              <Button className="mt-3" onClick={startUpload} disabled={uploading}>
                {uploading ? "Uploading…" : `Upload ${pendingFiles.length} photo(s)`}
              </Button>
            )}

            {uploadStates.length > 0 && (
              <div className="mt-3">
                {uploadStates.map((state) => (
                  <div key={state.id} className="gallery-upload-row" title={state.error}>
                    <span className="flex-grow-1">{state.fileName}</span>
                    {state.isFreePreview && <Badge bg="info">Free preview</Badge>}
                    <Badge
                      bg={
                        state.status === "ready"
                          ? "success"
                          : state.status === "failed"
                            ? "danger"
                            : "secondary"
                      }
                    >
                      {state.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4" style={cardStyle}>
          <Card.Header className="d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
            <h5 className="mb-0">
              Photos ({readyCount}/{items.length})
            </h5>
            {accessType === "paid" && (
              <Button size="sm" onClick={savePreviews} disabled={savingPreviews}>
                {savingPreviews ? "Saving…" : `Save Free Previews (${selectedPreviewIds.length}/${MAX_FREE_PREVIEWS})`}
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            {items.length === 0 ? (
              <div className="text-muted text-center py-4">No photos uploaded yet.</div>
            ) : (
              <div className="gallery-media-grid">
                {items.map((media) => {
                  const isSelected = selectedPreviewIds.includes(media.id);
                  const isReady = !media.status || media.status === "ready";
                  const isFailed = media.status === "failed";
                  return (
                    <div
                      key={media.id}
                      className="gallery-media-tile"
                      onClick={() => accessType === "paid" && togglePreviewSelection(media)}
                      role={accessType === "paid" && isReady ? "button" : undefined}
                      title={isFailed ? media.processingError : undefined}
                      style={{
                        backgroundColor: media.placeholder?.dominantColor || "#eef1f5",
                      }}
                    >
                      {media.contentUrl && (
                        // eslint-disable-next-line jsx-a11y/alt-text
                        <img src={media.contentUrl} alt={album.name} />
                      )}
                      {media.isFreePreview && isReady && (
                        <span className="gallery-media-tile__badge">Free</span>
                      )}
                      {!isReady && !isFailed && (
                        <div className="gallery-media-tile__status">
                          {media.status === "queued" ? "Queued" : "Processing…"}
                        </div>
                      )}
                      {isFailed && (
                        <div
                          className="gallery-media-tile__status"
                          style={{ background: "rgba(176,0,32,0.7)" }}
                        >
                          Failed
                        </div>
                      )}
                      {accessType === "paid" && isReady && (
                        <Form.Check
                          className="gallery-media-tile__check"
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => togglePreviewSelection(media)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AlbumDetail;
